import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { BOAT_TYPES, loadBoatTypes, getSidePreferenceForBoatType } from '../../utils/boatTypes';
import RiggingEditor from './RiggingEditor';

const CrewModal = ({
  isOpen,
  crew,
  trainingGroups,
  onClose,
  onCrewChange,
  onSubmit,
  mode = 'create', // 'create' or 'edit'
}) => {
  const [boatTypesLoaded, setBoatTypesLoaded] = useState(false);
  const [currentSidePreference, setCurrentSidePreference] = useState(null);
  const [riggingValidation, setRiggingValidation] = useState({ isValid: true });

  // Memoize boat type to prevent unnecessary re-renders
  const boatType = useMemo(() => (crew && crew.boat_type) || '', [crew?.id, crew?.boat_type]);

  // Load boat types on component mount
  useEffect(() => {
    if (isOpen && !boatTypesLoaded) {
      loadBoatTypes().then(() => {
        setBoatTypesLoaded(true);
      });
    }
  }, [isOpen, boatTypesLoaded]);

  // Update side preference when boat type changes
  useEffect(() => {
    if (boatType && boatTypesLoaded) {
      const sidePref = getSidePreferenceForBoatType(boatType);
      setCurrentSidePreference(sidePref);
    }
  }, [boatType, boatTypesLoaded]);

  // Reset side preference when boat type is empty
  useEffect(() => {
    if (!boatType && boatTypesLoaded) {
      setCurrentSidePreference(null);
    }
  }, [boatType, boatTypesLoaded]);

  // Handle rigging configuration change - memoized to prevent unnecessary re-renders
  const handleRiggingChange = useCallback(
    (riggingConfig) => {
      if (crew) {
        onCrewChange({ ...crew, rigging_config: riggingConfig });
      }
    },
    [crew, onCrewChange]
  );

  // Handle rigging validation change - memoized to prevent unnecessary re-renders
  const handleRiggingValidationChange = useCallback((validation) => {
    setRiggingValidation(validation);
  }, []);

  // Handle boat type change and reset rigging if switching between sweep/sculling
  const handleBoatTypeChange = useCallback(
    (newBoatType) => {
      if (!crew) return;

      const oldSidePref = currentSidePreference?.isStrict;
      const newSidePref = getSidePreferenceForBoatType(newBoatType)?.isStrict;

      // Reset rigging if switching between sweep and sculling, or if new boat type doesn't support rigging
      const shouldResetRigging = oldSidePref !== newSidePref || !newSidePref;

      // Update crew with new boat type and potentially cleared rigging
      onCrewChange({
        ...crew,
        boat_type: newBoatType,
        rigging_config: shouldResetRigging ? null : crew.rigging_config,
      });

      // Update validation state based on new boat type
      if (shouldResetRigging) {
        if (!newSidePref) {
          // Non-strict boat type - no rigging required, so validation is valid
          setRiggingValidation({ isValid: true });
        } else {
          // Strict boat type but rigging was cleared - validation is invalid until user configures
          setRiggingValidation({ isValid: false });
        }
      }
    },
    [crew, currentSidePreference, onCrewChange]
  );

  // Check if form is valid for submission
  const isFormValid = () => {
    if (!crew) return false;

    const hasName = crew.name && crew.name.trim();
    const hasBoatType = crew.boat_type;

    // For strict side preference boats, rigging must be valid
    // For non-strict boats, rigging validation is not required
    const isRiggingValid = !currentSidePreference?.isStrict || riggingValidation.isValid;

    return hasName && hasBoatType && isRiggingValid;
  };

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isFormValid() && crew) {
      onSubmit();
    }
  };

  const isEditMode = mode === 'edit';
  const title = isEditMode ? 'Edit Crew' : 'Create New Crew';
  const submitButtonText = isEditMode ? 'Save Changes' : 'Create Crew';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
      onClick={onClose}
    >
      <div
        className="max-h-[90vh] w-full max-w-2xl overflow-hidden rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-800">{title}</h2>
          <button
            className="flex h-8 w-8 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
            onClick={onClose}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="max-h-[calc(90vh-140px)] space-y-6 overflow-y-auto p-6">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Crew Name</label>
              <input
                type="text"
                placeholder="e.g., Bravo 4+, Alpha 8+"
                value={crew?.name || ''}
                onChange={(e) => crew && onCrewChange({ ...crew, name: e.target.value })}
                required
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Boat Type</label>
              <select
                value={crew?.boat_type || ''}
                onChange={(e) => crew && handleBoatTypeChange(e.target.value)}
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500"
                disabled={!boatTypesLoaded}
              >
                <option value="">Select boat type...</option>
                {Object.entries(BOAT_TYPES).map(([key, type]) => (
                  <option key={key} value={key}>
                    {type.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Training Group</label>
              <select
                value={crew?.training_group_id || ''}
                onChange={(e) =>
                  crew && onCrewChange({ ...crew, training_group_id: e.target.value || null })
                }
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500"
              >
                <option value="">No training group</option>
                {trainingGroups.map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Description</label>
              <textarea
                placeholder="Optional description of the crew..."
                value={crew?.description || ''}
                onChange={(e) => crew && onCrewChange({ ...crew, description: e.target.value })}
                rows={3}
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Experimental Crew</label>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <button
                    type="button"
                    onClick={() =>
                      onCrewChange({ ...crew, is_experimental: !(crew?.is_experimental || false) })
                    }
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                      crew?.is_experimental || false ? 'bg-blue-600' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        crew?.is_experimental || false ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                  <span className="text-sm text-gray-700">
                    {crew?.is_experimental || false ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </div>
              <p className="text-xs text-gray-500">
                Experimental crews allow specific seat assignments for testing lineups.
              </p>
            </div>

            {currentSidePreference && crew?.boat_type && (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Side Preference Setting
                </label>
                <div
                  className={`rounded-md border p-3 ${
                    currentSidePreference.isStrict
                      ? 'border-green-200 bg-green-50'
                      : 'border-blue-200 bg-blue-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div
                        className={`h-3 w-3 rounded-full ${
                          currentSidePreference.isStrict ? 'bg-green-500' : 'bg-blue-400'
                        }`}
                      ></div>
                      <span className="ml-2 text-sm font-medium text-gray-700">
                        {currentSidePreference.isStrict
                          ? 'Strict Side Preference'
                          : 'Flexible Side Preference'}
                      </span>
                    </div>
                    <span className="text-xs text-gray-500">
                      {currentSidePreference.isStrict
                        ? 'Auto-set for sweep boats'
                        : 'Auto-set for sculling boats'}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-gray-600">{currentSidePreference.description}</p>
                  <p className="mt-1 text-xs text-gray-500">
                    Category: {currentSidePreference.category}
                  </p>
                </div>
              </div>
            )}

            {!currentSidePreference && crew?.boat_type && (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Side Preference Setting
                </label>
                <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="h-3 w-3 rounded-full bg-gray-400"></div>
                      <span className="ml-2 text-sm font-medium text-gray-700">
                        Loading side preference...
                      </span>
                    </div>
                  </div>
                  <p className="mt-1 text-xs text-gray-600">
                    This setting is automatically managed based on the boat type and cannot be
                    manually changed.
                  </p>
                </div>
              </div>
            )}

            {/* Rigging Editor */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Rigging Configuration
              </label>
              <RiggingEditor
                boatType={crew?.boat_type}
                riggingConfig={crew?.rigging_config}
                onRiggingChange={handleRiggingChange}
                onValidationChange={handleRiggingValidationChange}
                isStrictSidePreference={currentSidePreference?.isStrict || false}
                disabled={!currentSidePreference?.isStrict}
              />
            </div>

            {/* Conditional messages based on mode */}
            {!isEditMode && crew?.is_experimental && (
              <div className="rounded-md border border-blue-200 bg-blue-50 p-3">
                <div className="text-sm text-blue-800">
                  <strong>Experimental Crew:</strong> This crew will be used for testing lineups
                  with specific seat assignments.
                </div>
              </div>
            )}

            {isEditMode && crew?.is_experimental !== crew?.originalIsExperimental && (
              <div className="rounded-md border border-yellow-200 bg-yellow-50 p-3">
                <div className="text-sm text-yellow-800">
                  <strong>Note:</strong> Changing experimental status will affect how rower
                  assignments work:
                  <ul className="ml-4 mt-1 list-disc text-xs">
                    <li>
                      <strong>Experimental:</strong> Rowers are assigned to specific seats
                    </li>
                    <li>
                      <strong>Standard:</strong> Rowers are added without seat assignments
                    </li>
                  </ul>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-3 border-t border-gray-200 p-6">
            <button
              type="button"
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              disabled={!isFormValid()}
            >
              {submitButtonText}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

CrewModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  crew: PropTypes.object,
  trainingGroups: PropTypes.array.isRequired,
  onClose: PropTypes.func.isRequired,
  onCrewChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  mode: PropTypes.oneOf(['create', 'edit']),
};

export default CrewModal;
